import { useState, useRef, useEffect, useMemo } from 'react';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Minimize2,
  BookOpen,
  ExternalLink,
  Bot,
  ThumbsUp,
  ThumbsDown,
  User,
  Headphones,
  RotateCcw,
  LogOut,
  Plus,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { createChatService } from '@/services/chat.service';
import { useCustomerChatWebSocket } from '@/hooks/useCustomerChatWebSocket';
import { MarkdownRenderer } from './MarkdownRenderer';
import type { ChatWidgetConfig, ChatMessage, QuickReply } from './types';

// Message deduplication helper - tracks seen message IDs with timestamps
// to handle out-of-order arrivals and prevent duplicates from REST + WebSocket
const createMessageDeduplicator = () => {
  const seenMessages = new Map<string, { timestamp: number; message: ChatMessage }>();
  const MAX_CACHE_SIZE = 200;
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  return {
    // Check if message was already seen, return true if it's a duplicate
    isDuplicate: (messageId: string): boolean => {
      return seenMessages.has(messageId);
    },

    // Track a message as seen
    markSeen: (message: ChatMessage): void => {
      seenMessages.set(message.id, { timestamp: Date.now(), message });

      // Cleanup old entries if cache is too large
      if (seenMessages.size > MAX_CACHE_SIZE) {
        const now = Date.now();
        for (const [id, entry] of seenMessages.entries()) {
          if (now - entry.timestamp > CACHE_TTL_MS) {
            seenMessages.delete(id);
          }
        }
      }
    },

    // Clear all tracked messages (on session reset)
    clear: (): void => {
      seenMessages.clear();
    },
  };
};

interface ChatWidgetProps {
  config?: Partial<ChatWidgetConfig>;
}

const DEFAULT_CONFIG: ChatWidgetConfig = {
  tenantId: '11111111-1111-1111-1111-111111111111',
  title: 'Support Chat',
  subtitle: 'We typically reply within minutes',
  welcomeMessage: "Hello! I'm your AI assistant powered by our knowledge base. Ask me anything about our products and services, and I'll find the best answers for you.",
  botName: 'AI Assistant',
  primaryColor: '#4f46e5',
  mode: 'hybrid',
  requirePreChat: true,
  showSuggestions: true,
  showSources: true,
  enableFeedback: true,
  enableQuickReplies: true,
  enableMarkdown: true,
  position: 'bottom-right',
  offsetX: 24,
  offsetY: 24,
  preChatFields: [
    { name: 'name', label: 'Your Name', type: 'text', required: true, placeholder: 'John Doe' },
    { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'john@example.com' },
  ],
};

// Storage keys
// Client-side cap on a single chat message so users can't paste huge strings
// that render as oversized bubbles / bloat requests (#9668).
const MAX_MESSAGE_LENGTH = 2000;

const getStorageKey = (tenantId: string, key: string) => `chat_${tenantId}_${key}`;

// Clear all chat session data from localStorage
const clearSessionStorage = (tenantId: string) => {
  localStorage.removeItem(getStorageKey(tenantId, 'session_id'));
  localStorage.removeItem(getStorageKey(tenantId, 'conversation_id'));
  localStorage.removeItem(getStorageKey(tenantId, 'is_handoff'));
  // Keep visitor_id to maintain identity
};

// Helper function to format timestamp safely
const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Helper function to check if two dates are on different days
const isDifferentDay = (date1: string | undefined, date2: string | undefined): boolean => {
  if (!date1 || !date2) return false;
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() !== d2.toDateString();
  } catch {
    return false;
  }
};

// Format date for separator
const formatDateSeparator = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

export function ChatWidget({ config: userConfig }: ChatWidgetProps = {}) {
  // Merge user config with defaults
  const config = useMemo(() => ({ ...DEFAULT_CONFIG, ...userConfig }), [userConfig]);

  // Create tenant-specific chat service
  const chatService = useMemo(() => createChatService(config.tenantId), [config.tenantId]);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showPreChat, setShowPreChat] = useState(config.requirePreChat);
  const [preChatData, setPreChatData] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  const [isEndingChat, setIsEndingChat] = useState(false);
  const [isChatEnded, setIsChatEnded] = useState(false);  // Hide input after ticket creation
  const [isAgentConnected, setIsAgentConnected] = useState(false);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [isHandoffMode, setIsHandoffMode] = useState(false);
  // Guest pre-chat email OTP (verify the visitor's email before the chat starts).
  const [preChatOtpStep, setPreChatOtpStep] = useState(false);
  const [preChatOtpCode, setPreChatOtpCode] = useState('');
  const [preChatMaskedEmail, setPreChatMaskedEmail] = useState('');
  const [preChatSeconds, setPreChatSeconds] = useState(0);
  const [preChatResending, setPreChatResending] = useState(false);
  const [preChatOtpError, setPreChatOtpError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Which identity (customer id / email, or 'guest') owns the current chat.
  // Lets us start a fresh chat whenever the signed-in user changes. null = not
  // yet observed (distinguishes page-load/refresh hydration from a real switch).
  const prevChatOwnerRef = useRef<string | null>(null);
  const { customer, isAuthenticated, isHydrated } = useAuthStore();

  // Message deduplication to prevent duplicates from REST + WebSocket
  const messageDeduplicator = useRef(createMessageDeduplicator());

  // Track previous sessionId for WebSocket recovery
  const prevSessionIdRef = useRef<string | null>(null);

  // Track if chat is ready for messaging (WebSocket connected + joined session)
  const [isWsReady, setIsWsReady] = useState(false);

  // Countdown for the pre-chat email OTP.
  useEffect(() => {
    if (!preChatOtpStep || preChatSeconds <= 0) return;
    const id = window.setInterval(() => {
      setPreChatSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [preChatOtpStep, preChatSeconds]);

  // WebSocket connection for real-time agent messages
  const { isConnected, hasJoinedSession, sendTyping, rejoinSession } = useCustomerChatWebSocket({
    tenantId: config.tenantId,
    visitorId,
    sessionId,
    onMessage: (message) => {
      console.log('[ChatWidget] Received message via WebSocket:', message);
      // Validate message before processing
      if (!message?.id || !message?.content) {
        console.warn('[ChatWidget] Received invalid message:', message);
        return;
      }

      // Ignore customer/visitor messages via WebSocket - they're already added optimistically
      // This prevents duplicate messages when the server broadcasts back the customer's own message
      if (message.senderType === 'customer' || message.senderType === 'visitor') {
        console.log('[ChatWidget] Ignoring own message from WebSocket:', message.id);
        return;
      }

      // CRITICAL FIX: Use message deduplicator to prevent duplicates from REST + WebSocket
      if (messageDeduplicator.current.isDuplicate(message.id)) {
        console.log('[ChatWidget] Duplicate message detected, skipping:', message.id);
        return;
      }
      messageDeduplicator.current.markSeen(message);

      // Stop typing indicator when we receive a bot/agent response
      if (message.senderType === 'bot' || message.senderType === 'agent') {
        setIsTyping(false);
      }

      // Transform message to include proper quick replies from metadata
      const transformedMessage: ChatMessage = {
        ...message,
        senderName: message.senderType === 'bot' ? config.botName : message.senderName,
        quickReplies: (message as any).quick_replies || (message as any).quickReplies || (message.metadata as any)?.quick_replies,
        sources: (message as any).sources || (message.metadata as any)?.sources,
      };

      // Check if handoff was suggested by the AI
      const metadata = message.metadata as any;
      if (metadata?.handoff_suggested && config.enableQuickReplies) {
        // Ensure quick replies are shown for handoff confirmation
        if (!transformedMessage.quickReplies || transformedMessage.quickReplies.length === 0) {
          transformedMessage.quickReplies = [
            { id: 'confirm_handoff', label: 'Yes, connect me', value: 'Yes, please connect me to an agent' },
            { id: 'decline_handoff', label: 'No, continue chatting', value: 'No, I have another question' },
          ];
        }
      }

      // Add message to the list (avoid duplicates using both deduplicator and state check)
      setMessages((prev) => {
        if (prev.some(m => m.id === message.id)) {
          return prev;
        }
        return [...prev, transformedMessage];
      });

      // Handle handoff if needed
      if (metadata?.needs_handoff) {
        config.onHandoff?.();
        initiateHandoff();
      }

      config.onMessageReceived?.(transformedMessage);

      // Increment unread if chat is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    },
    onTyping: (data) => {
      if (data.userType === 'agent') {
        setIsTyping(data.isTyping);
      }
    },
    onAgentAssigned: (data) => {
      console.log('[ChatWidget] Agent assigned:', data);
      setIsAgentConnected(true);
      setAgentName(data.agentName || 'Support Agent');

      // Add system message about agent joining
      setMessages(prev => [...prev, {
        id: `system-agent-${Date.now()}`,
        content: `${data.agentName || 'A support agent'} has joined the chat.`,
        senderType: 'system',
        createdAt: new Date().toISOString(),
      }]);
    },
    onSessionEnded: (data) => {
      console.log('[ChatWidget] Session ended:', data);
      setIsAgentConnected(false);
      setIsHandoffMode(false);
      setIsChatEnded(true);  // Hide input when session ends

      // Add system message about session ending
      setMessages(prev => [...prev, {
        id: `system-end-${Date.now()}`,
        content: data.reason || 'The chat session has ended. Thank you for contacting us!',
        senderType: 'system',
        createdAt: new Date().toISOString(),
      }]);
    },
    onConnected: () => {
      console.log('[ChatWidget] WebSocket connected');
    },
    onDisconnected: () => {
      console.log('[ChatWidget] WebSocket disconnected');
    },
  });

  // Restore session from localStorage on mount so a page refresh keeps the
  // conversation and its history (#9684). This must run for GUESTS too — the
  // session/visitor ids are persisted for guests as well, but the old code
  // early-returned to the pre-chat form for any guest when requirePreChat was
  // on, discarding the restorable session and its history on every refresh.
  useEffect(() => {
    // Wait for the persisted auth store to finish rehydrating before deciding
    // ownership. On a refresh the store starts with the default guest identity
    // (isAuthenticated=false, customer=null) and only resolves the real logged-in
    // user asynchronously. Acting before that made an authenticated user's session
    // look "foreign", so it was wrongly cleared and history was lost on every
    // refresh (#9666). Re-runs once isHydrated flips true.
    if (!isHydrated) return;

    const storedVisitorId = localStorage.getItem(getStorageKey(config.tenantId, 'visitor_id'));
    const storedSessionId = localStorage.getItem(getStorageKey(config.tenantId, 'session_id'));
    const storedConversationId = localStorage.getItem(getStorageKey(config.tenantId, 'conversation_id'));
    const storedIsHandoff = localStorage.getItem(getStorageKey(config.tenantId, 'is_handoff'));

    // An existing session exists → restore it (guest or authenticated) and skip
    // the pre-chat form; the history-load effect then repopulates the messages.
    const storedOwner = localStorage.getItem(getStorageKey(config.tenantId, 'owner')) || 'guest';
    const currentOwner = customer?.id || customer?.email || (isAuthenticated ? 'auth' : 'guest');

    // Only restore a session that belongs to the CURRENT user. A session owned by
    // a different customer (or a guest session when now logged in, and vice versa)
    // must never be shown — start fresh instead (#9684 follow-up: chat leaked
    // across customer switches on a shared browser).
    if (storedVisitorId && storedSessionId && storedOwner === currentOwner) {
      setVisitorId(storedVisitorId);
      setSessionId(storedSessionId);
      if (storedConversationId) {
        setConversationId(storedConversationId);
      }
      if (storedIsHandoff === 'true') {
        setIsHandoffMode(true);
      }
      setShowPreChat(false);
      return;
    }

    // Foreign/stale session (owned by a different identity) → clear it fully so
    // the next session starts clean.
    if (storedSessionId && storedOwner !== currentOwner) {
      clearSessionStorage(config.tenantId);
      localStorage.removeItem(getStorageKey(config.tenantId, 'visitor_id'));
      localStorage.removeItem(getStorageKey(config.tenantId, 'owner'));
    }

    // No (valid) session to restore → guests with requirePreChat see the pre-chat form.
    if (!isAuthenticated && config.requirePreChat) {
      setShowPreChat(true);
    }
  }, [config.tenantId, isAuthenticated, config.requirePreChat, isHydrated]);

  // Load message history when session is restored
  useEffect(() => {
    if (sessionId && visitorId && messages.length === 0) {
      loadMessageHistory();
    }
  }, [sessionId, visitorId]);

  // Track WebSocket readiness - ensure we're connected and joined before enabling chat
  useEffect(() => {
    const ready = isConnected && hasJoinedSession && !!sessionId;
    setIsWsReady(ready);
    if (ready) {
      console.log('[ChatWidget] WebSocket ready - connected and joined session:', sessionId);
    }
  }, [isConnected, hasJoinedSession, sessionId]);

  // CRITICAL FIX: Detect session ID changes and trigger WebSocket rejoin
  // This handles the case where a new session is created after error recovery
  useEffect(() => {
    if (!sessionId || !isConnected) return;

    // Check if session changed
    if (prevSessionIdRef.current && prevSessionIdRef.current !== sessionId) {
      console.log('[ChatWidget] Session ID changed from', prevSessionIdRef.current, 'to', sessionId);
      // Trigger WebSocket rejoin for the new session
      rejoinSession(sessionId).then((success) => {
        if (success) {
          console.log('[ChatWidget] Successfully rejoined new session:', sessionId);
        } else {
          console.error('[ChatWidget] Failed to rejoin new session:', sessionId);
        }
      });
    }

    prevSessionIdRef.current = sessionId;
  }, [sessionId, isConnected, rejoinSession]);

  const loadMessageHistory = async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);
      const history = await chatService.getSessionMessages(sessionId);
      if (history && history.length > 0) {
        // The initial AI welcome greeting is a client-side message (never persisted
        // server-side), so restoring only the server history dropped it on refresh
        // (#9688). Re-add it at the top of the restored history to match the live
        // layout, unless the stored history already begins with a bot greeting.
        const startsWithBot = history[0]?.senderType === 'bot';
        if (startsWithBot) {
          setMessages(history);
        } else {
          const welcome: ChatMessage = {
            id: 'welcome',
            content: config.welcomeMessage || DEFAULT_CONFIG.welcomeMessage!,
            senderType: 'bot',
            senderName: config.botName,
            createdAt: history[0]?.createdAt || new Date().toISOString(),
          };
          setMessages([welcome, ...history]);
        }
      } else {
        // Add welcome message if no history
        setMessages([{
          id: 'welcome',
          content: config.welcomeMessage || DEFAULT_CONFIG.welcomeMessage!,
          senderType: 'bot',
          senderName: config.botName,
          createdAt: new Date().toISOString(),
        }]);
      }
    } catch (err: any) {
      console.error('Failed to load message history:', err);

      // Check if the error is because the session is ended or not found
      const errorMessage = err?.response?.data?.message || err?.message || '';
      if (errorMessage.includes('ended') || errorMessage.includes('not found') || err?.response?.status === 404) {
        console.log('[ChatWidget] Session is ended or not found, clearing session data');
        // Clear the old session data and start fresh
        clearSessionStorage(config.tenantId);
        setSessionId(null);
        setConversationId(null);
        setIsHandoffMode(false);
        setShowPreChat(config.requirePreChat && !isAuthenticated);
      }

      // Add welcome message on error
      setMessages([{
        id: 'welcome',
        content: config.welcomeMessage || DEFAULT_CONFIG.welcomeMessage!,
        senderType: 'bot',
        senderName: config.botName,
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !showPreChat) {
      inputRef.current?.focus();
    }
  }, [isOpen, showPreChat]);

  // Clear unread count when chat is opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Start a FRESH chat whenever the signed-in identity changes — guest -> login,
  // login -> guest, or one customer -> another — so a customer never inherits the
  // previous user's conversation/history on a shared browser (#9684 follow-up).
  // The first run is page-load/refresh hydration, not a switch; skip it and let
  // the restore effect decide (it validates the stored session's owner).
  useEffect(() => {
    // Don't evaluate an owner "switch" until the auth store has rehydrated —
    // otherwise the transient guest->real-user transition on a refresh is
    // misread as a customer switch and wipes the restored session (#9666).
    if (!isHydrated) return;

    const currentOwner = customer?.id || customer?.email || (isAuthenticated ? 'auth' : 'guest');
    const isFirstRun = prevChatOwnerRef.current === null;
    const prevOwner = prevChatOwnerRef.current;
    prevChatOwnerRef.current = currentOwner;
    if (isFirstRun || currentOwner === prevOwner) {
      return;
    }

    console.log('[ChatWidget] Chat owner changed - starting a fresh chat session');

    // Wipe the previous identity's session completely (incl. visitor id + owner).
    clearSessionStorage(config.tenantId);
    localStorage.removeItem(getStorageKey(config.tenantId, 'visitor_id'));
    localStorage.removeItem(getStorageKey(config.tenantId, 'owner'));
    messageDeduplicator.current.clear();
    prevSessionIdRef.current = null;

    setVisitorId(null);
    setSessionId(null);
    setConversationId(null);
    setIsHandoffMode(false);
    setIsAgentConnected(false);
    setAgentName(null);
    setMessages([]);
    setError(null);
    setSuggestedQuestions([]);
    setShowPreChat(config.requirePreChat && !isAuthenticated);

    // For an authenticated user, auto-start a fresh session immediately.
    if (isAuthenticated && customer) {
      initializeSession({ name: customer.name, email: customer.email });
    }
  }, [isAuthenticated, customer, config.tenantId, isHydrated]);

  // Initialize session when authenticated user opens chat
  useEffect(() => {
    if (isAuthenticated && customer && isOpen && !visitorId) {
      initializeSession({ name: customer.name, email: customer.email });
    }
  }, [isAuthenticated, customer, isOpen, visitorId]);

  const initializeSession = async (visitorInfo: Record<string, string>) => {
    try {
      setIsLoading(true);
      setError(null);

      // CRITICAL FIX: Clear deduplicator for fresh session
      messageDeduplicator.current.clear();

      const session = await chatService.initSession(visitorInfo);
      setVisitorId(session.visitorId);
      localStorage.setItem(getStorageKey(config.tenantId, 'visitor_id'), session.visitorId);
      setShowPreChat(false);

      // Start conversation (this creates a chat session)
      const conversation = await chatService.startConversation(session.visitorId, visitorInfo);

      // CRITICAL FIX: Unify session ID handling
      // The server returns the same ID for both conversation.id and session.id
      // We use a single unified ID for both REST API calls (as conversationId) and WebSocket (as sessionId)
      const unifiedSessionId = conversation?.id || conversation?.sessionId || `conv_${Date.now()}`;

      // Log if there's a mismatch (shouldn't happen but helps debug)
      if (conversation?.sessionId && conversation.id && conversation.sessionId !== conversation.id) {
        console.warn('[ChatWidget] Session ID mismatch detected:', {
          conversationId: conversation.id,
          sessionId: conversation.sessionId,
        });
      }

      // Set both conversationId and sessionId to the same value for consistency
      setConversationId(unifiedSessionId);
      setSessionId(unifiedSessionId);
      localStorage.setItem(getStorageKey(config.tenantId, 'conversation_id'), unifiedSessionId);
      localStorage.setItem(getStorageKey(config.tenantId, 'session_id'), unifiedSessionId);
      // Tag the session with its owner so it is only restored for the SAME user
      // and reset when a different customer/login uses this browser (#9684).
      localStorage.setItem(getStorageKey(config.tenantId, 'owner'), customer?.id || customer?.email || 'guest');

      console.log('[ChatWidget] Session initialized with unified ID:', unifiedSessionId);

      // Add welcome message
      setMessages([{
        id: 'welcome',
        content: config.welcomeMessage || DEFAULT_CONFIG.welcomeMessage!,
        senderType: 'bot',
        senderName: config.botName,
        createdAt: new Date().toISOString(),
      }]);

      // Load initial suggested questions
      if (config.showSuggestions) {
        try {
          const suggestions = await chatService.getSuggestions('help');
          setSuggestedQuestions(suggestions.slice(0, 3));
        } catch {
          setSuggestedQuestions([
            'How do I reset my password?',
            'What are your business hours?',
            'How can I track my order?',
          ]);
        }
      }

      config.onOpen?.();
    } catch (err) {
      console.error('Failed to initialize chat:', err);
      setError('Failed to connect to chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const preChatExpired = preChatOtpStep && preChatSeconds <= 0;
  const preChatTimerLabel = `${Math.floor(preChatSeconds / 60)}:${String(preChatSeconds % 60).padStart(2, '0')}`;

  const handlePreChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const missingFields = config.preChatFields?.filter(
      f => f.required && !preChatData[f.name]?.trim()
    );
    if (missingFields && missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    // If the form collects an email, verify it with a one-time code before
    // starting the chat. Configs without an email field keep the old behaviour.
    const email = (preChatData['email'] || '').trim();
    if (!email) {
      await initializeSession(preChatData);
      return;
    }

    setError(null);
    setPreChatOtpError('');
    setIsLoading(true);
    try {
      const res = await chatService.sendPreChatOtp(email, preChatData['name']);
      setPreChatMaskedEmail(res.maskedEmail);
      setPreChatSeconds(res.expiresInSeconds || 600);
      setPreChatOtpCode('');
      setPreChatOtpStep(true);
    } catch {
      setError('Could not send a verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPreChatOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setPreChatOtpError('');
    if (preChatExpired) {
      setPreChatOtpError('Your code has expired. Please request a new one.');
      return;
    }
    const email = (preChatData['email'] || '').trim();
    setIsLoading(true);
    const result = await chatService.verifyPreChatOtp(email, preChatOtpCode);
    setIsLoading(false);
    if (result.ok) {
      setPreChatOtpStep(false);
      await initializeSession(preChatData);
      return;
    }
    if (result.code === 'OTP_EXPIRED' || result.code === 'OTP_MAX_ATTEMPTS') {
      setPreChatSeconds(0);
      setPreChatOtpError(
        result.code === 'OTP_MAX_ATTEMPTS'
          ? 'Too many incorrect attempts. Please request a new code.'
          : 'Your code has expired. Please request a new one.',
      );
    } else {
      setPreChatOtpCode('');
      setPreChatOtpError('Enter the correct code.');
    }
  };

  const handleResendPreChatOtp = async () => {
    const email = (preChatData['email'] || '').trim();
    setPreChatOtpError('');
    setPreChatResending(true);
    try {
      const res = await chatService.sendPreChatOtp(email, preChatData['name']);
      setPreChatMaskedEmail(res.maskedEmail);
      setPreChatSeconds(res.expiresInSeconds || 600);
      setPreChatOtpCode('');
    } catch {
      setPreChatOtpError('Could not send a new code. Please try again.');
    } finally {
      setPreChatResending(false);
    }
  };

  const backToPreChatDetails = () => {
    setPreChatOtpStep(false);
    setPreChatOtpCode('');
    setPreChatOtpError('');
    setPreChatSeconds(0);
  };

  const initiateHandoff = async () => {
    if (!visitorId || !conversationId) return;

    // The session was created when startConversation() was called, but it starts
    // in 'bot' status and is NOT in the agent queue (#9690). We must explicitly
    // ask the backend to escalate it into the waiting queue so an agent can pick
    // it up — a handoff should only happen when the customer requests it (or when
    // the AI escalates via needs_handoff).

    // Use the conversation ID as the session ID (they're the same from widget/conversations)
    const existingSessionId = sessionId || conversationId;

    if (existingSessionId) {
      // Puts this same session - bot conversation included - into the queue.
      await chatService.requestHandoff(existingSessionId, 'Customer requested a human agent');
      setSessionId(existingSessionId);
      localStorage.setItem(getStorageKey(config.tenantId, 'session_id'), existingSessionId);
      setIsHandoffMode(true);
      localStorage.setItem(getStorageKey(config.tenantId, 'is_handoff'), 'true');

      console.log('[ChatWidget] Handoff initiated for session:', existingSessionId);

      // Add system message
      setMessages(prev => [...prev, {
        id: `system-queue-${Date.now()}`,
        content: "You're now in the queue to speak with a support agent. Please wait while we connect you.",
        senderType: 'system',
        createdAt: new Date().toISOString(),
      }]);
    } else {
      // Fallback: create a new session if somehow we don't have one
      try {
        const chatSession = await chatService.createLiveChatSession(visitorId, preChatData);

        if (chatSession?.id) {
          setSessionId(chatSession.id);
          localStorage.setItem(getStorageKey(config.tenantId, 'session_id'), chatSession.id);
          setIsHandoffMode(true);
          localStorage.setItem(getStorageKey(config.tenantId, 'is_handoff'), 'true');

          console.log('[ChatWidget] Live chat session created:', chatSession.id);

          setMessages(prev => [...prev, {
            id: `system-queue-${Date.now()}`,
            content: "You're now in the queue to speak with a support agent. Please wait while we connect you.",
            senderType: 'system',
            createdAt: new Date().toISOString(),
          }]);
        }
      } catch (err) {
        console.error('Failed to create live chat session:', err);
        setMessages(prev => [...prev, {
          id: `system-error-${Date.now()}`,
          content: "Sorry, we couldn't connect you to an agent right now. Please try again.",
          senderType: 'system',
          createdAt: new Date().toISOString(),
        }]);
      }
    }
  };

  const handleSendMessage = async (e?: React.FormEvent, quickReplyValue?: string) => {
    e?.preventDefault();

    const content = (quickReplyValue || inputValue.trim()).slice(0, MAX_MESSAGE_LENGTH);
    if (!content || !conversationId || !visitorId || isSending) return;

    setInputValue('');
    setIsSending(true);
    setSuggestedQuestions([]);

    // Add message optimistically
    const tempMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      content,
      senderType: 'customer',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      // If in handoff mode with active session, send via WebSocket-backed API
      if (isHandoffMode && sessionId) {
        try {
          // This endpoint returns { visitorMessage, aiMessage }, NOT a single
          // message. Treating the whole response as the message left content
          // undefined, which crashed the renderer ("Something went wrong" after
          // an agent joins — #9685). Extract the visitor message (fall back to the
          // raw response for older shapes) and always keep the original content.
          const sent = (await chatService.sendSessionMessage(sessionId, visitorId, content)) as any;
          const visitorMsg = (sent && sent.visitorMessage ? sent.visitorMessage : sent) as ChatMessage;
          const confirmed: ChatMessage = { ...visitorMsg, content: visitorMsg?.content ?? content };
          setMessages(prev => prev.map(m => m.id === tempMessage.id ? confirmed : m));
          config.onMessageSent?.(confirmed);
          return;
        } catch (handoffErr: any) {
          const errMsg = handoffErr?.response?.data?.message || '';
          if (errMsg.includes('ended') || errMsg.includes('Cannot send message')) {
            console.log('[ChatWidget] Handoff session ended, switching back to bot mode');
            clearSessionStorage(config.tenantId);
            setIsHandoffMode(false);
            setIsAgentConnected(false);
            setSessionId(null);

            // Show message about session ending
            setMessages(prev => [...prev.filter(m => m.id !== tempMessage.id), {
              id: `system-ended-${Date.now()}`,
              content: 'Your previous chat session has ended. Please start a new conversation.',
              senderType: 'system',
              createdAt: new Date().toISOString(),
            }]);
            setIsSending(false);
            return;
          }
          throw handoffErr; // Re-throw if it's a different error
        }
      }

      // Send message via Channel Gateway - now returns AI response synchronously
      // The Channel Gateway waits for AI Engine response and returns both messages
      try {
        // Show typing indicator while waiting for AI response
        setIsTyping(true);

        const response = await chatService.sendMessage(conversationId, visitorId, content);

        // Replace temp message with confirmed visitor message
        const visitorMsg = response.visitorMessage;
        setMessages(prev => prev.map(m => m.id === tempMessage.id ? visitorMsg : m));
        config.onMessageSent?.(visitorMsg);

        // Add AI message if present (received synchronously from REST API)
        // Only render an AI bubble when it has ACTUAL content. After escalation
        // to a human agent the backend returns an empty aiMessage object; that
        // must not render a blank AI bubble (#9670).
        if (response.aiMessage && (response.aiMessage.content || '').trim()) {
          console.log('[ChatWidget] AI response received synchronously:', response.aiMessage.id);
          // CRITICAL FIX: Mark as seen to prevent duplicate from WebSocket
          messageDeduplicator.current.markSeen(response.aiMessage);

          // Transform AI message to map snake_case to camelCase fields
          const transformedAiMessage: ChatMessage = {
            ...response.aiMessage,
            senderName: config.botName,
            // Map quick_replies (snake_case from backend) to quickReplies (camelCase for frontend)
            quickReplies: (response.aiMessage as any).quick_replies || (response.aiMessage as any).quickReplies,
            sources: (response.aiMessage as any).sources,
          };

          console.log('[ChatWidget] Transformed AI message quickReplies:', transformedAiMessage.quickReplies);

          setMessages(prev => {
            // Check if AI message already exists (from WebSocket)
            const exists = prev.some(m => m.id === response.aiMessage!.id);
            if (exists) {
              return prev;
            }
            return [...prev, transformedAiMessage];
          });

          // Escalate on the REST path too, mirroring the WebSocket handler, so a
          // TYPED "connect me to an agent" shows the queue indicator (via
          // initiateHandoff) just like the headset button does (#9665). Without
          // this the synchronous REST response carried needs_handoff but nothing
          // acted on it, so typed escalation was a silent dead-end.
          const aiMeta = (response.aiMessage as any).metadata;
          if (aiMeta?.needs_handoff && !isHandoffMode) {
            config.onHandoff?.();
            initiateHandoff();
          }
        } else if (!response.aiMessage) {
          // No AI answer came back AT ALL — show a fallback so the chat never goes
          // silent/unresponsive (#9682). An empty aiMessage object (e.g. after
          // escalation to a human agent) falls through here and renders nothing (#9670).
          setMessages(prev => [...prev, {
            id: `fallback-${Date.now()}`,
            content: "I'm sorry, I'm having trouble answering that right now. Please try again, or type \"agent\" to connect with a person.",
            senderType: 'bot',
            senderName: config.botName,
            createdAt: new Date().toISOString(),
          }]);
        }

        // Stop typing indicator
        setIsTyping(false);

      } catch (sendErr: any) {
        const errMsg = sendErr?.response?.data?.message || sendErr?.message || '';
        console.log('[ChatWidget] sendMessage error:', errMsg);
        setIsTyping(false);

        if (errMsg.includes('ended') || errMsg.includes('Cannot send message')) {
          console.log('[ChatWidget] Session ended, starting new conversation');
          // CRITICAL FIX: Clear old session and deduplicator for fresh start
          clearSessionStorage(config.tenantId);
          messageDeduplicator.current.clear();

          // Create a new conversation with unified session ID
          const newConversation = await chatService.startConversation(visitorId, preChatData);
          const unifiedNewSessionId = newConversation?.id || newConversation?.sessionId || `conv_${Date.now()}`;

          // Set both IDs to the same value for consistency
          setConversationId(unifiedNewSessionId);
          setSessionId(unifiedNewSessionId);
          localStorage.setItem(getStorageKey(config.tenantId, 'conversation_id'), unifiedNewSessionId);
          localStorage.setItem(getStorageKey(config.tenantId, 'session_id'), unifiedNewSessionId);

          console.log('[ChatWidget] New session created with unified ID:', unifiedNewSessionId);

          // Retry sending the message with the new conversation
          setIsTyping(true);
          const retryResponse = await chatService.sendMessage(unifiedNewSessionId, visitorId, content);
          setMessages(prev => prev.map(m => m.id === tempMessage.id ? retryResponse.visitorMessage : m));
          config.onMessageSent?.(retryResponse.visitorMessage);

          // Add AI message if present
          if (retryResponse.aiMessage) {
            // Mark as seen to prevent duplicate from WebSocket
            messageDeduplicator.current.markSeen(retryResponse.aiMessage);
            // Transform AI message
            const transformedRetryAi: ChatMessage = {
              ...retryResponse.aiMessage,
              senderName: config.botName,
              quickReplies: (retryResponse.aiMessage as any).quick_replies || (retryResponse.aiMessage as any).quickReplies,
              sources: (retryResponse.aiMessage as any).sources,
            };
            setMessages(prev => [...prev, transformedRetryAi]);
          }
          setIsTyping(false);
        } else {
          throw sendErr; // Re-throw other errors
        }
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);

      // Rate limited by the server (#9669): the visitor is sending messages too
      // quickly. Don't treat this as a failure/session-ended — drop the optimistic
      // bubble, give the typed text back so nothing is lost, and show a gentle
      // notice instead of the generic "something went wrong" fallback.
      if (err?.response?.status === 429) {
        setIsTyping(false);
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        setInputValue(content);
        const retryAfter = err?.response?.data?.retryAfter;
        setMessages(prev => [...prev, {
          id: `system-ratelimit-${Date.now()}`,
          content: err?.response?.data?.message
            || `You're sending messages too quickly. Please wait ${retryAfter ? `${retryAfter} second${retryAfter === 1 ? '' : 's'}` : 'a moment'} and try again.`,
          senderType: 'system',
          createdAt: new Date().toISOString(),
        }]);
        return;
      }

      // Check if the error is because the session ended
      const errorMessage = err?.response?.data?.message || err?.message || '';
      if (errorMessage.includes('ended') || errorMessage.includes('Cannot send message')) {
        console.log('[ChatWidget] Session ended, creating new session');
        // CRITICAL FIX: Clear old session and deduplicator for fresh start
        clearSessionStorage(config.tenantId);
        messageDeduplicator.current.clear();
        setIsHandoffMode(false);

        // Try to create a new session and resend the message
        try {
          const conversation = await chatService.startConversation(visitorId, preChatData);
          // Use unified session ID for both conversationId and sessionId
          const unifiedNewSessionId = conversation?.id || conversation?.sessionId || `conv_${Date.now()}`;

          setConversationId(unifiedNewSessionId);
          setSessionId(unifiedNewSessionId);
          localStorage.setItem(getStorageKey(config.tenantId, 'conversation_id'), unifiedNewSessionId);
          localStorage.setItem(getStorageKey(config.tenantId, 'session_id'), unifiedNewSessionId);

          console.log('[ChatWidget] New session created with unified ID:', unifiedNewSessionId);

          // Resend the message
          const response = await chatService.sendMessage(unifiedNewSessionId, visitorId, content);
          setMessages(prev => prev.map(m => m.id === tempMessage.id ? response.visitorMessage : m));
          config.onMessageSent?.(response.visitorMessage);

          // Add AI message if present
          if (response.aiMessage) {
            // Mark as seen to prevent duplicate from WebSocket
            messageDeduplicator.current.markSeen(response.aiMessage);
            // Transform AI message
            const transformedRecoveryAi: ChatMessage = {
              ...response.aiMessage,
              senderName: config.botName,
              quickReplies: (response.aiMessage as any).quick_replies || (response.aiMessage as any).quickReplies,
              sources: (response.aiMessage as any).sources,
            };
            setMessages(prev => [...prev, transformedRecoveryAi]);
          }
          return;
        } catch (retryErr) {
          console.error('Failed to recreate session:', retryErr);
        }
      }

      // Mark the user's message failed AND post a bot fallback, so the
      // conversation always shows a response instead of hanging silently (#9682).
      setMessages(prev => [
        ...prev.map(m => (m.id === tempMessage.id ? { ...m, failed: true } : m)),
        {
          id: `fallback-${Date.now()}`,
          content: "I'm sorry, I'm having trouble responding right now. Please try again in a moment, or type \"agent\" to connect with a person.",
          senderType: 'bot',
          senderName: config.botName,
          createdAt: new Date().toISOString(),
        },
      ]);
      setIsTyping(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickReply = (reply: QuickReply) => {
    handleSendMessage(undefined, reply.value);
  };

  const handleFeedback = async (messageId: string, feedback: 'positive' | 'negative') => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedback } : m
    ));

    // Find the message and submit feedback
    const message = messages.find(m => m.id === messageId);
    if (message) {
      await chatService.submitFeedback(messageId, feedback);
      config.onFeedback?.(messageId, feedback);
    }
  };

  const handleHandoff = async () => {
    config.onHandoff?.();
    if (!isHandoffMode) {
      await initiateHandoff();
    }
  };

  // Start a completely fresh chat session
  const startNewChat = async () => {
    console.log('[ChatWidget] Starting new chat - clearing all session data');

    // Clear ALL session data including visitor ID
    localStorage.removeItem(getStorageKey(config.tenantId, 'visitor_id'));
    localStorage.removeItem(getStorageKey(config.tenantId, 'session_id'));
    localStorage.removeItem(getStorageKey(config.tenantId, 'conversation_id'));
    localStorage.removeItem(getStorageKey(config.tenantId, 'is_handoff'));

    // CRITICAL FIX: Clear message deduplicator for fresh session
    messageDeduplicator.current.clear();
    prevSessionIdRef.current = null;

    // Reset all state
    setVisitorId(null);
    setSessionId(null);
    setConversationId(null);
    setIsHandoffMode(false);
    setIsAgentConnected(false);
    setAgentName(null);
    setMessages([]);
    setError(null);
    setSuggestedQuestions([]);
    setIsChatEnded(false);  // Reset so input is shown again

    // Show pre-chat form
    setShowPreChat(config.requirePreChat);
  };

  // End the current chat session and create a ticket
  const endChat = async () => {
    if (!sessionId && !conversationId) {
      // No active session, just close
      setShowEndChatConfirm(false);
      setIsOpen(false);
      return;
    }

    setIsEndingChat(true);
    try {
      const result = await chatService.endSession(sessionId || conversationId!, 'User ended chat');

      // Add system message about chat ending.
      // Only show a ticket reference when it's a REAL, findable ticket number
      // (e.g. "TKT-20260280", which contains a hyphen) — not a truncated internal
      // UUID prefix, which previously made the message imply a ticket the user
      // could never find (#9691).
      const hasRealTicketNumber = !!result.ticketNumber && result.ticketNumber.includes('-');
      const endMessage: ChatMessage = {
        id: `end-${Date.now()}`,
        content: result.ticketCreated
          ? (hasRealTicketNumber
              ? `Chat ended. A support ticket (#${result.ticketNumber}) has been created. Our team will follow up with you soon.`
              : 'Chat ended. A support ticket has been created and our team will follow up with you soon.')
          : 'Chat ended. Thank you for contacting us!',
        senderType: 'system' as const,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, endMessage]);

      // Mark chat as ended - this hides the input area
      setIsChatEnded(true);

      // Clear session data
      clearSessionStorage(config.tenantId);
      setSessionId(null);
      setConversationId(null);
      setIsHandoffMode(false);
      setIsAgentConnected(false);
    } catch (err) {
      console.error('Failed to end chat:', err);
      // The chat is still open on the server: keep it open here too.
      setMessages(prev => [...prev, {
        id: `end-failed-${Date.now()}`,
        content: "We couldn't end the chat right now. Please try again.",
        senderType: 'system' as const,
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setIsEndingChat(false);
      setShowEndChatConfirm(false);
    }
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (!newIsOpen) {
        config.onClose?.();
        // An ended chat is over: the next time the window opens it is a new one.
        if (isChatEnded) void startNewChat();
      }
    }
  };

  // Position styles
  const positionStyles = config.position === 'bottom-left'
    ? { left: config.offsetX, bottom: config.offsetY }
    : { right: config.offsetX, bottom: config.offsetY };

  // Closed state - floating button with tooltip
  if (!isOpen) {
    return (
      <div style={positionStyles} className="fixed z-50 group">
        {/* Tooltip */}
        <div className="absolute bottom-[72px] right-0 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap shadow-xl pointer-events-none transform group-hover:translate-y-0 translate-y-1">
          <span className="font-medium">Need help?</span> Chat with us!
          <div className="absolute -bottom-1.5 right-7 w-3 h-3 bg-gray-900 rotate-45"></div>
        </div>
        <button
          onClick={toggleChat}
          className="w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl hover:bg-primary-700 hover:scale-105 transition-all duration-200 flex items-center justify-center"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold ring-2 ring-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      style={positionStyles}
      className={`fixed z-50 flex flex-col ${isMinimized ? 'h-16' : 'h-[650px]'} w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden transition-all duration-300 ease-in-out dark:bg-gray-900 dark:border-gray-700`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 via-primary-600 to-primary-700 text-white px-5 py-4 flex items-center justify-between flex-shrink-0 shadow-md">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center ring-2 ring-white/40">
              {isAgentConnected ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
            </div>
            {/* Online indicator */}
            <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-primary-600 ${isWsReady || isConnected ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`}></span>
          </div>
          <div>
            <h3 className="font-semibold text-base leading-tight">
              {isAgentConnected ? agentName || 'Support Agent' : config.title}
            </h3>
            <div className="flex items-center gap-2 text-xs text-white/80 mt-0.5">
              {isHandoffMode && (
                <>
                  {isConnected ? (
                    <span className="flex items-center gap-1.5">
                      {/* Once the socket is connected the customer IS connected to live chat;
                          show the real state (waiting vs agent joined) rather than a
                          stuck "Connecting..." that depended on a fragile join-ack (#9686). */}
                      {isAgentConnected ? 'Agent connected' : 'Waiting for agent...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      Reconnecting...
                    </span>
                  )}
                </>
              )}
              {!isHandoffMode && (
                <span className="flex items-center gap-1.5">
                  {isConnected ? 'Online • Connected' : 'Connecting...'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isMinimized && config.mode === 'hybrid' && !isHandoffMode && (
            <button
              onClick={handleHandoff}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Talk to agent"
              title="Talk to a human agent"
            >
              <Headphones className="h-4 w-4" />
            </button>
          )}
          {!isMinimized && (sessionId || conversationId) && messages.length > 1 && (
            <button
              onClick={() => setShowEndChatConfirm(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="End chat"
              title="End chat and create ticket"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
          {!isMinimized && (
            <button
              onClick={loadMessageHistory}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Refresh messages"
              title="Refresh messages"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          {!isMinimized && (sessionId || conversationId) && (
            <button
              onClick={startNewChat}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="New chat"
              title="Start a new chat"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label={isMinimized ? 'Expand' : 'Minimize'}
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setIsOpen(false); config.onClose?.(); }}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* End Chat Confirmation Dialog */}
      {showEndChatConfirm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-2xl">
          <div className="bg-white rounded-xl p-6 mx-4 max-w-sm shadow-xl dark:bg-gray-800">
            <h4 className="text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100">End Chat?</h4>
            <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
              This will end your current chat session. A support ticket will be created for our team to follow up.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndChatConfirm(false)}
                disabled={isEndingChat}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 dark:text-gray-200 dark:border-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={endChat}
                disabled={isEndingChat}
                className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isEndingChat ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Ending...
                  </>
                ) : (
                  'End Chat'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isMinimized && (
        <>
          {/* Pre-chat form */}
          {showPreChat && !isAuthenticated ? (
            <div className="flex-1 p-6 flex flex-col justify-center overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
              {preChatOtpStep ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2 dark:text-gray-100">Verify your email</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      We sent a 6-digit code to{' '}
                      <span className="font-medium text-gray-900 dark:text-gray-100">{preChatMaskedEmail}</span>.
                    </p>
                  </div>

                  <form onSubmit={handleVerifyPreChatOtp} className="space-y-4">
                    <input
                      id="chat-otp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      autoFocus
                      disabled={preChatExpired}
                      value={preChatOtpCode}
                      onChange={(e) => setPreChatOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 text-center text-2xl tracking-[0.4em] font-mono border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700 disabled:opacity-50"
                      placeholder="000000"
                      maxLength={6}
                    />

                    <div className="flex items-center justify-between text-sm">
                      {preChatExpired ? (
                        <span className="text-red-600">Code expired</span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          Expires in <span className="font-medium tabular-nums">{preChatTimerLabel}</span>
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleResendPreChatOtp}
                        disabled={preChatResending || (!preChatExpired && preChatSeconds > 0)}
                        className="text-primary-600 hover:underline font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                      >
                        {preChatResending ? 'Sending…' : 'Resend code'}
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || preChatOtpCode.length !== 6 || preChatExpired}
                      className="w-full py-3.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <MessageCircle className="h-5 w-5" />
                          Verify & Start Chat
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={backToPreChatDetails}
                      className="w-full py-2 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Back
                    </button>
                  </form>

                  {preChatOtpError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-sm text-red-600 text-center">{preChatOtpError}</p>
                    </div>
                  )}
                </>
              ) : (
              <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-8 w-8 text-primary-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2 dark:text-gray-100">Welcome! 👋</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">Please provide your details to start chatting with our AI assistant.</p>
              </div>

              <form onSubmit={handlePreChatSubmit} className="space-y-4">
                {config.preChatFields?.map((field) => (
                  <div key={field.name}>
                    <label htmlFor={`chat-${field.name}`} className="block text-sm font-medium text-gray-800 mb-1.5 dark:text-gray-200">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        id={`chat-${field.name}`}
                        value={preChatData[field.name] || ''}
                        onChange={(e) => setPreChatData(prev => ({ ...prev, [field.name]: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700"
                        required={field.required}
                      >
                        <option value="">Select...</option>
                        {field.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`chat-${field.name}`}
                        type={field.type}
                        value={preChatData[field.name] || ''}
                        onChange={(e) => setPreChatData(prev => ({ ...prev, [field.name]: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700 placeholder:text-gray-500 dark:placeholder:text-gray-400"
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-5 w-5" />
                      Start Chat
                    </>
                  )}
                </button>
              </form>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-600 text-center">{error}</p>
                </div>
              )}

              <p className="text-xs text-gray-400 text-center mt-6 dark:text-gray-500">
                Already have an account? <a href="/login" className="text-primary-600 hover:underline font-medium">Sign in</a> for a faster experience.
              </p>
              </>
              )}
            </div>
          ) : isLoading ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
              {/* Skeleton loader for messages */}
              <div className="flex justify-start">
                <div className="flex gap-2 max-w-[85%]">
                  <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse"></div>
                  <div className="space-y-2">
                    <div className="h-16 w-48 bg-gray-200 rounded-2xl rounded-bl-sm animate-pulse"></div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <div className="h-10 w-32 bg-primary-200 rounded-2xl rounded-br-sm animate-pulse"></div>
              </div>
              <div className="flex justify-start">
                <div className="flex gap-2 max-w-[85%]">
                  <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse"></div>
                  <div className="space-y-2">
                    <div className="h-20 w-56 bg-gray-200 rounded-2xl rounded-bl-sm animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-5 space-y-1 bg-gray-50/50 dark:bg-gray-900/50">
                {messages.map((message, index) => {
                  const isUser = message.senderType === 'customer' || message.senderType === 'visitor';
                  const isSystem = message.senderType === 'system';
                  const isBot = message.senderType === 'bot';
                  const isAgent = message.senderType === 'agent';
                  const prevMessage = index > 0 ? messages[index - 1] : null;
                  const showDateSeparator = index === 0 || isDifferentDay(prevMessage?.createdAt, message.createdAt);
                  const isSameSenderAsPrev = prevMessage && prevMessage.senderType === message.senderType;

                  return (
                    <div key={message.id}>
                      {/* Date Separator */}
                      {showDateSeparator && (
                        <div className="flex items-center justify-center my-4">
                          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                          <span className="px-3 text-xs font-medium text-gray-400 dark:text-gray-500">
                            {formatDateSeparator(message.createdAt)}
                          </span>
                          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                        </div>
                      )}

                      {/* Message Row */}
                      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${isSameSenderAsPrev && !showDateSeparator ? 'mt-1' : 'mt-4'}`}>
                        {/* User Message - Simple right-aligned structure */}
                        {isUser ? (
                          <div className="max-w-[75%] flex flex-col items-end">
                            {/* Message Bubble */}
                            <div
                              className={`
                                bg-primary-600 text-white rounded-2xl rounded-br-md px-4 py-2.5
                                ${message.failed ? 'opacity-60' : ''}
                              `}
                            >
                              <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                              {message.failed && (
                                <p className="text-xs text-red-200 mt-1.5 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-red-300 rounded-full"></span>
                                  Failed to send
                                </p>
                              )}
                            </div>
                            {/* Timestamp */}
                            <span className="text-[10px] text-gray-400 mt-1 mr-1 dark:text-gray-500">
                              {formatTime(message.createdAt)}
                            </span>
                          </div>
                        ) : (
                          /* Bot/Agent/System Message */
                          <div className={`max-w-[80%] ${!isSystem ? 'flex gap-2.5' : ''}`}>
                            {/* Bot/Agent Avatar - only show for first message in a group */}
                            {(isBot || isAgent) && (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isSameSenderAsPrev && !showDateSeparator ? 'invisible' : ''} ${isBot ? 'bg-gradient-to-br from-primary-100 to-primary-200' : 'bg-gradient-to-br from-blue-100 to-blue-200'}`}>
                                {isBot ? (
                                  <Bot className="h-4 w-4 text-primary-600" />
                                ) : (
                                  <User className="h-4 w-4 text-blue-600" />
                                )}
                              </div>
                            )}

                            <div className="flex-1 space-y-1.5">
                              {/* Sender name - only show for first message in a group */}
                              {(isBot || isAgent) && !isSameSenderAsPrev && (
                                <p className={`text-xs font-medium ml-1 ${isBot ? 'text-primary-600' : 'text-blue-600'}`}>
                                  {message.senderName || (isBot ? 'AI Assistant' : 'Support Agent')}
                                </p>
                              )}

                              {/* Message Bubble */}
                              <div
                                className={`
                                  ${isSystem
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200 text-center rounded-xl'
                                    : isBot
                                    ? 'bg-white text-gray-800 rounded-2xl rounded-bl-md shadow-sm border border-gray-100 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700'
                                    : 'bg-blue-50 text-gray-800 rounded-2xl rounded-bl-md border border-blue-100 dark:bg-blue-900/30 dark:text-gray-100 dark:border-blue-800'
                                  }
                                  px-4 py-2.5
                                  ${message.failed ? 'opacity-60' : ''}
                                `}
                              >
                              {/* Message content with markdown */}
                              {config.enableMarkdown ? (
                                <MarkdownRenderer content={message.content} className="text-[14px] leading-relaxed" />
                              ) : (
                                <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                              )}
                            </div>

                            {/* Timestamp and Feedback Row */}
                            {!isSystem && (
                              <div className="flex items-center gap-2 px-1">
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {formatTime(message.createdAt)}
                                </span>

                                {/* Feedback buttons for bot messages */}
                                {config.enableFeedback && isBot && message.id !== 'welcome' && (
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      onClick={() => handleFeedback(message.id, 'positive')}
                                      className={`p-1 rounded-full transition-all ${
                                        message.feedback === 'positive'
                                          ? 'text-green-600 bg-green-100'
                                          : 'text-gray-300 hover:text-green-500 hover:bg-green-50'
                                      }`}
                                      title="Helpful"
                                    >
                                      <ThumbsUp className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => handleFeedback(message.id, 'negative')}
                                      className={`p-1 rounded-full transition-all ${
                                        message.feedback === 'negative'
                                          ? 'text-red-600 bg-red-100'
                                          : 'text-gray-300 hover:text-red-500 hover:bg-red-50'
                                      }`}
                                      title="Not helpful"
                                    >
                                      <ThumbsDown className="h-3 w-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Article Sources */}
                            {message.sources && message.sources.length > 0 && (
                              <div className="bg-gray-100/80 rounded-xl p-3 space-y-1.5 dark:bg-gray-800/50">
                                <p className="text-[11px] text-gray-500 flex items-center gap-1.5 font-medium uppercase tracking-wide dark:text-gray-400">
                                  <BookOpen className="h-3 w-3" />
                                  Sources
                                </p>
                                {message.sources.map((source) => (
                                  <a
                                    key={source.id}
                                    href={`/articles/${source.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-xs text-primary-600 hover:text-primary-700 py-1 px-2 rounded-lg hover:bg-white/50 transition-colors dark:hover:bg-gray-700/50"
                                  >
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">{source.title}</span>
                                  </a>
                                ))}
                              </div>
                            )}

                            {/* Quick Replies */}
                            {message.quickReplies && message.quickReplies.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {message.quickReplies.map((reply) => (
                                  <button
                                    key={reply.id}
                                    onClick={() => handleQuickReply(reply)}
                                    className="text-xs px-3 py-1.5 bg-white border border-gray-200 text-primary-600 rounded-full hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-all shadow-sm dark:bg-gray-800 dark:border-gray-700"
                                  >
                                    {reply.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex justify-start mt-4">
                    <div className="flex gap-2.5 items-end">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                        {isAgentConnected ? <User className="h-4 w-4 text-primary-600" /> : <Bot className="h-4 w-4 text-primary-600" />}
                      </div>
                      <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex gap-1 items-center">
                          <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5">
                          {isAgentConnected ? 'Agent is typing...' : 'AI is thinking...'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions */}
              {suggestedQuestions.length > 0 && !isSending && !isTyping && !isHandoffMode && (
                <div className="px-4 py-3 bg-gradient-to-t from-white to-gray-50/80 border-t border-gray-100 dark:from-gray-900 dark:to-gray-800/80 dark:border-gray-700">
                  <p className="text-[11px] text-gray-500 mb-2 font-medium uppercase tracking-wide dark:text-gray-400">
                    Suggested questions
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setInputValue(question);
                          setSuggestedQuestions([]);
                          inputRef.current?.focus();
                        }}
                        className="text-xs px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-all truncate max-w-[180px] border border-gray-200 hover:border-primary-200 shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input - Hidden when chat has ended */}
              {isChatEnded ? (
                <div className="p-4 border-t border-gray-200/80 flex-shrink-0 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      This chat session has ended.
                    </p>
                    <button
                      onClick={startNewChat}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                    >
                      <Plus className="h-4 w-4" />
                      Start New Chat
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-200/80 flex-shrink-0 bg-white dark:bg-gray-900 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        ref={inputRef}
                        type="text"
                        maxLength={MAX_MESSAGE_LENGTH}
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          if (isHandoffMode && sessionId) {
                            sendTyping(true);
                          }
                        }}
                        onBlur={() => {
                          if (isHandoffMode && sessionId) {
                            sendTyping(false);
                          }
                        }}
                        placeholder={isHandoffMode ? "Message agent..." : "Type a message..."}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-400 text-sm bg-gray-50 dark:bg-gray-800 dark:border-gray-700 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                        disabled={isSending || isLoading}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || isSending}
                      className="w-10 h-10 bg-primary-600 text-white rounded-xl flex items-center justify-center hover:bg-primary-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      {isSending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 text-center mt-2 dark:text-gray-500">
                    Powered by AI • {isAgentConnected ? 'Connected to agent' : 'Instant responses'}
                  </p>
                </form>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Export default config for reference
export { DEFAULT_CONFIG as defaultChatWidgetConfig };
